// Components/Services/FullHomeRemodel/HomeRemodelOtherServices.jsx
import React from "react";
import { Link } from "react-router-dom";
import { FiHome, FiLayers, FiTool } from "react-icons/fi";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const gridStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
};

const services = [
  {
    title: "ADUs & Ground-Up Custom Homes",
    description: "From garage conversions to fully custom ground-up builds — permitted and built right.",
    image: "/Images/ADU and Custom Homes/ADU and Custom Homes 3.jpeg",
    icon: <FiHome className="text-[#212529] w-6 h-6" />,
    link: "/services/custom-homes",
    points: [
      "Garage & basement ADU conversions",
      "Detached accessory dwelling units",
      "Ground-up custom home builds",
      "Full permit & design management",
    ],
  },
  {
    title: "HOA & Commercial Projects",
    description: "Large-scale building envelope and commercial tenant improvements.",
    image: "/Images/HOA/HOA 1.jpeg",
    icon: <FiLayers className="text-[#212529] w-6 h-6" />,
    link: "/services/hoa-commercial",
    points: [
      "Building envelope rehabilitation",
      "Common area reconstruction",
      "Tenant improvements",
      "Insurance & litigation support",
    ],
  },
  {
    title: "Specialty: Takeover Projects",
    description: "Stalled build? Failed contractor? We step in and finish it right.",
    image: "/Images/takeoverprojects.png",
    icon: <FiTool className="text-[#212529] w-6 h-6" />,
    link: "/services/takeover-projects",
    points: [
      "Failed contractor recovery",
      "Code violation correction",
      "Construction defect remediation",
      "Budget recovery & replanning",
    ],
  },
];

const ServiceCard = ({ service }) => (
  <motion.div variants={fadeUp} className="relative flex h-full">
    <div className="bg-[#212529] rounded-xl shadow-lg overflow-hidden flex flex-col h-full w-full transition-all duration-500 hover:scale-105">

      {/* Image */}
      <div className="h-44 w-full overflow-hidden flex-shrink-0">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
      </div>

      {/* Content */}
      <div className="p-5 pt-10 text-center flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-white min-h-[56px] flex items-center justify-center">
          {service.title}
        </h3>
        <p className="text-sm text-[#adb5bd] mt-2 mb-4">
          {service.description}
        </p>

        {/* Points */}
        <ul className="space-y-1.5 mb-5 flex-1 flex flex-col items-start w-fit mx-auto">
          {service.points.map((point, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#dee2e6]">
              <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
              {point}
            </li>
          ))}
        </ul>

        <div className="mt-1 pt-1">
                 <Link
                   to={service.link}
                   className="inline-block bg-[#f8f9fa] text-[#212529] text-sm font-medium px-4 py-2 rounded-full hover:bg-[#e9ecef] transition"
                 >
                   View Service
                 </Link>
               </div>
      </div>
    </div>

    {/* Icon badge */}
    <div className="absolute top-[10rem] left-1/2 transform -translate-x-1/2 bg-[#f8f9fa] rounded-full p-3 shadow-lg z-20">
      {service.icon}
    </div>
  </motion.div>
);

const HomeRemodelOtherServices = () => {
  return (
    <section className="bg-[#f8f9fa] py-12">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Header */}
        <motion.div className="max-w-7xl mx-auto px-4 text-center" variants={staggerContainer}>
          <motion.p variants={fadeUp} className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-2">
            What Else We Do
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#212529]">
            Our Other Services
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 text-[#6c757d] max-w-2xl mx-auto">
            From ADUs and custom homes to HOA reconstruction and takeover projects — Ashlar handles complex, high-value builds from rough to perfect.
          </motion.p>
        </motion.div>

        {/* Cards */}
        <motion.div className="mt-10 max-w-[1200px] mx-auto px-4" variants={gridStagger}>
          <div className="flex flex-col md:flex-row justify-center gap-6 items-stretch">
            {services.map((service, index) => (
              <div key={index} className="flex-1 min-w-[260px] max-w-[360px] mx-auto md:mx-0">
                <ServiceCard service={service} />
              </div>
            ))}
          </div>
        </motion.div>

      </motion.div>
    </section>
  );
};

export default HomeRemodelOtherServices;